/**
 * Safe logging helpers to prevent log injection (CWE-117).
 * Sanitizes newlines and control characters so user-controlled data
 * cannot forge log entries or break log monitors.
 */

// eslint-disable-next-line no-control-regex -- Intentional: detecting control chars to prevent log injection (CWE-117)
const CONTROL_AND_NEWLINES = /[\r\n\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g;
const REPLACEMENT = '[NL]';

/**
 * Sanitize a string for safe logging or storage (prevents log injection).
 * Export for use in error loggers and other non-console sinks.
 */
export function sanitizeStringForLog(value: string | null | undefined): string {
  if (value == null) return '';
  return String(value).replace(CONTROL_AND_NEWLINES, REPLACEMENT);
}

function sanitizeForLog(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  let str: string;
  if (typeof value === 'string') {
    str = value;
  } else if (typeof value === 'object') {
    try {
      str = JSON.stringify(value);
    } catch {
      str = '[object]';
    }
  } else {
    str = String(value);
  }
  return str.replace(CONTROL_AND_NEWLINES, REPLACEMENT);
}

/**
 * Log message and args with sanitized string representations.
 * Use for any log that may include user-controlled or API response data.
 */
export function safeLog(message: string, ...args: unknown[]): void {
  const safeMessage = sanitizeForLog(message);
  const safeArgs = args.map(sanitizeForLog);
  console.log(safeMessage, ...safeArgs);
}

/**
 * Log error message and args with sanitized string representations.
 * Use for any console.error that may include user-controlled or API response data.
 */
export function safeError(message: string, ...args: unknown[]): void {
  const safeMessage = sanitizeForLog(message);
  const safeArgs = args.map(sanitizeForLog);
  console.error(safeMessage, ...safeArgs);
}

/**
 * Log warning message and args with sanitized string representations.
 * Use for any console.warn that may include user-controlled or API response data.
 */
export function safeWarn(message: string, ...args: unknown[]): void {
  const safeMessage = sanitizeForLog(message);
  const safeArgs = args.map(sanitizeForLog);
  console.warn(safeMessage, ...safeArgs);
}

/**
 * Debug-level log with sanitized string representations. In typical browser
 * DevTools configurations (e.g. Chrome's default log-level filter) debug
 * output is hidden unless the Verbose/Debug level is enabled, so it stays
 * diagnosable without alarming production consoles.
 *
 * Use for EXPECTED, user-recoverable failures (e.g. a mistyped OTP) where an
 * error-level log would be noise — never for genuinely unexpected failures,
 * which must keep using safeError (REK-2022 console hygiene).
 */
export function safeDebug(message: string, ...args: unknown[]): void {
  const safeMessage = sanitizeForLog(message);
  const safeArgs = args.map(sanitizeForLog);
  console.debug(safeMessage, ...safeArgs);
}
